/**
 * Before/after viewer for a remediation proposal: two panes (stacked on phones) plus the exact unified diff.
 * Added/removed lines carry a visually-hidden label, so the change is not conveyed by colour alone (WCAG 1.4.1).
 * Diff text is customer/provider content: LTR, isolated with <bdi>, never interpreted as HTML.
 */
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import { invertDiff, parseDiff, showValue } from './resolve-flow';

export function DiffViewer({ before, after, diff, inverted = false }: { before: unknown; after: unknown; diff: string; inverted?: boolean }) {
  const { t } = useApp();
  const f = useFormat();
  const lines = inverted ? invertDiff(parseDiff(diff)) : parseDiff(diff);
  const [left, right] = inverted ? [after, before] : [before, after];
  return (
    <div className="ar-diff" data-testid="diff-viewer">
      <div className="ar-diff__panes">
        <figure className="ar-diff__pane" data-side="before">
          <figcaption>{t(inverted ? 'audit-remediation.actions.diff.now' : 'audit-remediation.actions.diff.before')}</figcaption>
          <pre dir="ltr"><bdi>{f.text(showValue(left)) || t('audit-remediation.actions.diff.empty')}</bdi></pre>
        </figure>
        <figure className="ar-diff__pane" data-side="after">
          <figcaption>{t(inverted ? 'audit-remediation.actions.diff.restored' : 'audit-remediation.actions.diff.after')}</figcaption>
          <pre dir="ltr"><bdi>{f.text(showValue(right)) || t('audit-remediation.actions.diff.empty')}</bdi></pre>
        </figure>
      </div>
      {lines.length ? (
        <details className="ar-diff__exact">
          <summary>{t('audit-remediation.actions.diff.exact')}</summary>
          <ol className="ar-diff__lines" dir="ltr" aria-label={t('audit-remediation.actions.diff.exact')}>
            {lines.map((l, i) => (
              <li key={i} data-kind={l.kind}>
                {l.kind === 'add' || l.kind === 'del' ? <span className="qm-sr-only">{t(`audit-remediation.actions.diff.${l.kind}`)} </span> : null}
                <span aria-hidden="true" className="ar-diff__sign">{l.kind === 'add' ? '+' : l.kind === 'del' ? '-' : ' '}</span>
                <code><bdi>{f.text(l.text)}</bdi></code>
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </div>
  );
}
